import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';

type Author = { id: number; name: string };
type TopicRow = {
  id: number; title: string; body: string; pinned: boolean; locked: boolean;
  removed: boolean; createdAt: string; replies: number; author: Author; mine: boolean;
};
type Post = {
  id: number; body: string; createdAt: string; updatedAt: string;
  removed: boolean; author: Author; mine: boolean;
};

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** The board for one course. */
export function CourseDiscussions() {
  const { courseId } = useParams();
  const [data, setData] = useState<{ moderates: boolean; items: TopicRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const load = useCallback(async () => {
    try { setData(await api.get(`/discussions/courses/${courseId}/topics`)); }
    catch (e) { setError((e as ApiError).message); }
  }, [courseId]);

  useEffect(() => { void load(); }, [load]);

  if (error) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const start = async () => {
    try {
      await api.post(`/discussions/courses/${courseId}/topics`, { title, body });
      setTitle(''); setBody(''); setStarting(false);
      await load();
    } catch (e) { setError((e as ApiError).message); }
  };

  return (
    <>
      <div className="page-head">
        <h1 className="h1">Discussion</h1>
        <div className="page-head__actions">
          <Link className="btn btn--ghost" to={`/courses/${courseId}`}>Back to the course</Link>
          <button className="btn btn--primary" onClick={() => setStarting(!starting)}>
            {starting ? 'Cancel' : 'Start a topic'}
          </button>
        </div>
      </div>

      {starting && (
        <section className="panel">
          <div className="form">
            <label><span>Title</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255}
                placeholder="What is your question?" />
            </label>
            <label><span>Your question</span>
              <textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
            </label>
            <button className="btn btn--primary" disabled={!title.trim() || !body.trim()} onClick={start}>
              Post it
            </button>
          </div>
        </section>
      )}

      {data.items.length === 0 && (
        <p className="notice">
          Nothing here yet. Start a topic and your tutor and the others on this course will see it.
        </p>
      )}

      <ul className="cardlist">
        {data.items.map((t) => (
          <li key={t.id} className={`cardlist__item ${t.removed ? 'is-removed' : ''}`}>
            <Link to={`/discussions/topics/${t.id}`} className="cardlist__main">
              <strong>
                {t.pinned && <span className="pill pill--quiet">Pinned</span>}{' '}
                {t.locked && <span className="pill pill--quiet">Closed</span>}{' '}
                {t.title}
              </strong>
              <span className="muted small">
                {t.author.name} · {when(t.createdAt)} · {t.replies} {t.replies === 1 ? 'reply' : 'replies'}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}

/** One topic and its replies. */
export function DiscussionTopic() {
  const { topicId } = useParams();
  const [data, setData] = useState<{
    moderates: boolean; courseId: number;
    topic: TopicRow & { author: Author | null };
    posts: Post[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    try { setData(await api.get(`/discussions/topics/${topicId}`)); }
    catch (e) { setError((e as ApiError).message); }
  }, [topicId]);

  useEffect(() => { void load(); }, [load]);

  if (error) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const act = async (fn: () => Promise<unknown>) => {
    try { await fn(); setError(null); await load(); }
    catch (e) { setError((e as ApiError).message); }
  };

  const { topic, moderates } = data;

  return (
    <>
      <div className="page-head">
        <h1 className="h1">{topic.title}</h1>
        <Link className="btn btn--ghost" to={`/courses/${data.courseId}/discussion`}>Back to the board</Link>
      </div>

      {moderates && (
        <p className="toolbar">
          <button className="btn btn--ghost" onClick={() => act(() =>
            api.patch(`/discussions/topics/${topic.id}`, { pinned: !topic.pinned }))}>
            {topic.pinned ? 'Unpin' : 'Pin to the top'}
          </button>
          <button className="btn btn--ghost" onClick={() => act(() =>
            api.patch(`/discussions/topics/${topic.id}`, { locked: !topic.locked }))}>
            {topic.locked ? 'Open for replies' : 'Close to replies'}
          </button>
          <button className="btn btn--ghost" onClick={() => act(() =>
            api.patch(`/discussions/topics/${topic.id}`, { removed: !topic.removed }))}>
            {topic.removed ? 'Put the topic back' : 'Remove the topic'}
          </button>
        </p>
      )}

      <article className={`bubble ${topic.removed ? 'is-removed' : ''}`}>
        <header className="bubble__head">
          <strong>{topic.author?.name ?? 'Somebody'}</strong>
          <span className="muted small"> · {when(topic.createdAt)}</span>
        </header>
        <p className="bubble__body">{topic.body}</p>
      </article>

      {data.posts.map((p) => (
        <article key={p.id} className={`bubble ${p.mine ? 'bubble--mine' : ''} ${p.removed ? 'is-removed' : ''}`}>
          <header className="bubble__head">
            <strong>{p.author.name}</strong>
            <span className="muted small"> · {when(p.createdAt)}</span>
            {p.updatedAt !== p.createdAt && <span className="muted small"> · edited</span>}
            {p.removed && <span className="pill pill--quiet">Removed</span>}
          </header>

          {editing === p.id ? (
            <div className="form">
              <textarea rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} />
              <p className="toolbar">
                <button className="btn btn--primary" onClick={() => act(async () => {
                  await api.patch(`/discussions/posts/${p.id}`, { body: draft });
                  setEditing(null);
                })}>Save</button>
                <button className="btn btn--ghost" onClick={() => setEditing(null)}>Cancel</button>
              </p>
            </div>
          ) : (
            <p className="bubble__body">{p.body}</p>
          )}

          {editing !== p.id && (p.mine || moderates) && (
            <p className="toolbar">
              {(!p.removed || moderates) && (
                <button className="btn btn--ghost btn--small"
                  onClick={() => { setEditing(p.id); setDraft(p.body); }}>Edit</button>
              )}
              {moderates && (
                <button className="btn btn--ghost btn--small" onClick={() => act(() =>
                  api.post(`/discussions/posts/${p.id}/removal`, { removed: !p.removed }))}>
                  {p.removed ? 'Put it back' : 'Remove'}
                </button>
              )}
            </p>
          )}
        </article>
      ))}

      {topic.locked && !moderates ? (
        <p className="notice">This topic is closed to replies.</p>
      ) : topic.removed ? (
        <p className="notice">This topic has been removed.</p>
      ) : (
        <section className="panel">
          <div className="form">
            <label><span>Reply</span>
              <textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} />
            </label>
            <button className="btn btn--primary" disabled={!reply.trim()} onClick={() => act(async () => {
              await api.post(`/discussions/topics/${topic.id}/posts`, { body: reply });
              setReply('');
            })}>Post reply</button>
          </div>
        </section>
      )}
    </>
  );
}
